import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateAgencyParams,
  CreateCandidateParams,
  CreateCandidateResponse,
  CreateOnboardingByIdParams,
  CreateOnboardingByIdResponse,
  CreateProspectParams,
  CreateProspectParamsResponse,
  DeleteAgencyProspectByIdsParams,
  DeleteAgencyProspectByIdsResponse,
  DeleteCandidateParams,
  DeleteCandidateResponse,
  DeleteProspectByIdParams,
  DeleteProspectByIdResponse,
  DeleteProspectByIdsParams,
  DeleteProspectByIdsResponse,
  GetAgencyProspectListParams,
  GetAgencyProspectListResponse,
  GetCandidateListParams,
  GetCandidateListResponse,
  GetOnboardingByProspectIdParams,
  GetOnboardingByProspectIdResponse,
  GetProspectListParams,
  GetProspectListResponse,
  ProgressOnboardingParams,
  ProgressOnboardingResponse,
  ProgressProspectParams,
  ProgressProspectResponse,
  ProgressRegistrationAgencyParams,
  ProgressRegistrationAgencyResponse,
  ProgressRegistrationParams,
  ProgressRegistrationResponse,
  ResendProspectParams,
  ResendProspectResponse,
  UpdateAgencyParams,
  UpdateAgencyResponse,
  UpdateCandidateByIdParams,
  UpdateCandidateByIdResponse,
  UpdateOnboardingByIdParams,
  UpdateOnboardingByIdResponse,
  UpdateProspectByIdParams,
  UpdateProspectByIdResponse,
  UpdateSuccessOnboardingByIdParams,
  UpdateSuccessOnboardingByIdResponse,
} from '../types';

// The BE reads agencyIds/companyIds through a ParseArrayPipe, so they go out as
// a single comma-separated value — axios would otherwise repeat them as
// `companyIds[]=`, which the pipe never sees under that name.
const withListFilterParams = <
  T extends { companyIds?: string[]; agencyIds?: string[] },
>(
  params: T,
) => ({
  ...params,
  companyIds: params.companyIds?.length
    ? params.companyIds.join(',')
    : undefined,
  agencyIds: params.agencyIds?.length ? params.agencyIds.join(',') : undefined,
});

export const getCandidateList = createAsyncThunk<
  GetCandidateListResponse,
  GetCandidateListParams
>(
  'prospect/getCandidateList',
  async (params: GetCandidateListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.prospects.candidate, {
        params: withListFilterParams(params),
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createCandidate = createAsyncThunk<
  CreateCandidateResponse,
  CreateCandidateParams
>(
  'prospect/createCandidate',
  async (params: CreateCandidateParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.prospects.candidate,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateCandidateById = createAsyncThunk<
  UpdateCandidateByIdResponse,
  UpdateCandidateByIdParams
>(
  'prospect/updateCandidateById',
  async (params: UpdateCandidateByIdParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(
        `${endPoints.prospects.candidate}/${id}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const progressOnboardingByIds = createAsyncThunk<
  ProgressOnboardingResponse,
  ProgressOnboardingParams
>(
  'prospect/progressOnboardingByIds',
  async (params: ProgressOnboardingParams, { rejectWithValue }) => {
    try {
      const { ids } = params;
      const response = await jwtAxios.put(
        endPoints.prospects.progressOnboarding,
        ids,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const progressRegistrationByIds = createAsyncThunk<
  ProgressRegistrationResponse,
  ProgressRegistrationParams
>(
  'prospect/progressRegistrationByIds',
  async (params: ProgressRegistrationParams, { rejectWithValue }) => {
    try {
      const { ids } = params;
      const response = await jwtAxios.put(
        endPoints.prospects.progressCandidate,
        ids,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteCandidateByIds = createAsyncThunk<
  DeleteCandidateResponse,
  DeleteCandidateParams
>(
  'prospect/deleteCandidateByIds',
  async (params: DeleteCandidateParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.prospects.candidate}/delete`,
        { data: params },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAgencyProspectList = createAsyncThunk<
  GetAgencyProspectListResponse,
  GetAgencyProspectListParams
>(
  'prospect/getAgencyProspectList',
  async (params: GetAgencyProspectListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.prospects.agency, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createAgencyProspect = createAsyncThunk<
  CreateCandidateResponse,
  CreateAgencyParams
>(
  'prospect/createAgencyProspect',
  async (params: CreateAgencyParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.prospects.agency, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateAgencyProspectById = createAsyncThunk<
  UpdateAgencyResponse,
  UpdateAgencyParams
>(
  'prospect/updateAgencyProspectById',
  async (params: UpdateAgencyParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(
        `${endPoints.prospects.agency}/${id}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const progressRegistrationAgencyProspectById = createAsyncThunk<
  ProgressRegistrationAgencyResponse,
  ProgressRegistrationAgencyParams
>(
  'prospect/progressRegistrationAgencyProspectById',
  async (params: ProgressRegistrationAgencyParams, { rejectWithValue }) => {
    try {
      const { ids } = params;
      const response = await jwtAxios.put(
        endPoints.prospects.progressAgency,
        ids,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getProspectList = createAsyncThunk<
  GetProspectListResponse,
  GetProspectListParams
>(
  'prospect/getProspectList',
  async (params: GetProspectListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.prospects.prospectOnboarding.main, {
        params: withListFilterParams(params),
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateProspectById = createAsyncThunk<
  UpdateProspectByIdResponse,
  UpdateProspectByIdParams
>(
  'prospect/updateProspectById',
  async (params: UpdateProspectByIdParams, { rejectWithValue }) => {
    try {
      const { id, payload } = params;
      const response = await jwtAxios.put(`${endPoints.prospects.prospectOnboarding.main}/${id}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resendProspectByIds = createAsyncThunk<
  ResendProspectResponse,
  ResendProspectParams
>(
  'prospect/resendProspectByIds',
  async (params: ResendProspectParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(endPoints.prospects.prospectOnboarding.resend, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const progressProspectByIds = createAsyncThunk<
  ProgressProspectResponse,
  ProgressProspectParams
>(
  'prospect/progressProspectByIds',
  async (params: ProgressProspectParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.put(endPoints.prospects.prospectOnboarding.progress, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteProspectById = createAsyncThunk<
  DeleteProspectByIdResponse,
  DeleteProspectByIdParams
>(
  'prospect/deleteProspectById',
  async (params: DeleteProspectByIdParams, { rejectWithValue }) => {
    try {
      const { id, isIreland = false } = params;
      if (isIreland) {
        const response = await jwtAxios.delete(`${endPoints.prospects.prospectOnboarding.bureau}/${id}`);
        return response.data;
      } else {
        const response = await jwtAxios.delete(`${endPoints.prospects.prospectOnboarding.main}/${id}`);
        return response.data;
      }

    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteProspectByIds = createAsyncThunk<
  DeleteProspectByIdsResponse,
  DeleteProspectByIdsParams
>(
  'prospect/deleteProspectByIds',
  async (params: DeleteProspectByIdsParams, { rejectWithValue }) => {
    try {
      const { ids, isIreland = false } = params;
      if (isIreland) {
        const response = await jwtAxios.delete(`${endPoints.prospects.prospectOnboarding.bureauDelete}`, { data: { ids } });
        return response.data;
      } else {
        const response = await jwtAxios.delete(`${endPoints.prospects.prospectOnboarding.delete}`, { data: { ids } });
        return response.data;
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getOnboardingByProspectId = createAsyncThunk<
  GetOnboardingByProspectIdResponse,
  GetOnboardingByProspectIdParams
>(
  'prospect/getOnboardingById',
  async (params: GetOnboardingByProspectIdParams, { rejectWithValue }) => {
    try {
      const { id } = params;
      const response = await jwtAxios.get(`${endPoints.prospects.prospectOnboarding.onboardingAdmin}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateOnboardingById = createAsyncThunk<
  UpdateOnboardingByIdResponse,
  UpdateOnboardingByIdParams
>(
  'prospect/updateOnboardingById',
  async (params: UpdateOnboardingByIdParams, { rejectWithValue }) => {
    try {
      const { id, payload, isIreland = false } = params;
      const endpoint = isIreland ? endPoints.prospects.prospectOnboarding.updateOnboardingAdmin : endPoints.prospects.prospectOnboarding.onboardingAdmin;
      const response = await jwtAxios.put(`${endpoint}/${id}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateSuccessOnboardingByProspectId = createAsyncThunk<
  UpdateSuccessOnboardingByIdResponse,
  UpdateSuccessOnboardingByIdParams
>(
  'prospect/updateSuccessOnboardingByProspectId',
  async (params: UpdateSuccessOnboardingByIdParams, { rejectWithValue }) => {
    try {
      // `hideToast` is a UI-only flag (read from action.meta.arg); keep it out
      // of the request body.
      const body = { ...(params as any) };
      delete body.hideToast;
      const response = await jwtAxios.put(endPoints.prospects.prospectOnboarding.onboardingAdminSuccess, body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createOnboardingByProspectId = createAsyncThunk<
  CreateOnboardingByIdResponse,
  CreateOnboardingByIdParams
>(
  'prospect/createOnboardingByProspectId',
  async (params: CreateOnboardingByIdParams, { rejectWithValue }) => {
    try {
      // `hideToast` is a UI-only flag (read from action.meta.arg); keep it out
      // of the request body.
      const body = { ...(params as any) };
      delete body.hideToast;
      const response = await jwtAxios.post(endPoints.prospects.prospectOnboarding.createOnboardingAdmin, body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createProspect = createAsyncThunk<
  CreateProspectParamsResponse,
  CreateProspectParams
>(
  'prospect/createProspect',
  async (params: CreateProspectParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.prospects.prospectOnboarding.main, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteAgencyProspectByIds = createAsyncThunk<
  DeleteAgencyProspectByIdsResponse,
  DeleteAgencyProspectByIdsParams
>(
  'prospect/deleteAgencyProspectByIds',
  async (params: DeleteAgencyProspectByIdsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(`${endPoints.prospects.deleteAgency}`, { data: params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getProspectPendingCount = createAsyncThunk<
  { countProspectPending: number; countDocumentIDPending: number },
  void
>(
  'prospect/getProspectPendingCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.prospects.prospectOnboarding.enableOnboarding);
      return {
        countProspectPending: response.data?.data?.countProspectPending || 0,
        countDocumentIDPending: response.data?.data?.countDocumentIDPending || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const prospectActs = {
  getCandidateList,
  createCandidate,
  updateCandidateById,
  progressOnboardingByIds,
  progressRegistrationByIds,
  deleteCandidateByIds,
  getAgencyProspectList,
  createAgencyProspect,
  updateAgencyProspectById,
  progressRegistrationAgencyProspectById,
  getProspectList,
  updateProspectById,
  resendProspectByIds,
  progressProspectByIds,
  deleteProspectById,
  deleteProspectByIds,
  getOnboardingByProspectId,
  updateOnboardingById,
  updateSuccessOnboardingByProspectId,
  createOnboardingByProspectId,
  createProspect,
  deleteAgencyProspectByIds,
  getProspectPendingCount,
};
